<h2 mat-dialog-title class="dialog-title">
  <mat-icon>analytics</mat-icon>
  <span>Manage Reference Range</span>
</h2>

<mat-dialog-content class="dialog-content" aria-live="polite">
  <div class="observation-banner">
    <div class="banner-block">
      <div class="label">Observation</div>
      <div class="value">{{ observationDisplay() }}</div>
      @if(observationCode()) {
        <div class="code-ref">{{ observationCode() }}</div>
      }
    </div>
    <div class="banner-block">
      <div class="label">Current Result</div>
      <div class="value result">{{ (observation | valueToString) || '—' }}</div>
    </div>
  </div>

  <mat-tab-group>
    <mat-tab label="Auto-Match Templates">
      <div class="tab-content" style="padding-top: 16px;">
        @if(loading) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }

        @if(!loading && suggestions.length === 0) {
          <div class="empty-state">
            <p>No saved reference range templates matched this observation.</p>
            <p class="muted">Create one from the Reference Range workspace, then try again.</p>
            <button mat-stroked-button color="primary" (click)="manageTemplates()">
              <mat-icon>open_in_new</mat-icon>
              <span>Manage Templates</span>
            </button>
          </div>
        } @else if (suggestions.length > 0) {
          <div class="table-wrapper">
            <table mat-table [dataSource]="suggestions" [trackBy]="trackByTemplate" class="mat-elevation-z2">
              <ng-container matColumnDef="type">
                <th mat-header-cell *matHeaderCellDef>Meaning</th>
                <td mat-cell *matCellDef="let template">
                  {{ template.range?.type?.text || template.range?.type?.coding?.[0]?.display || template.display }}
                </td>
              </ng-container>

              <ng-container matColumnDef="range">
                <th mat-header-cell *matHeaderCellDef>Range</th>
                <td mat-cell *matCellDef="let template">
                       <div style=" width: 280px;         
  white-space: nowrap;    
  overflow: hidden;       
  text-overflow: ellipsis;">

                  {{ rangeDescription(template.range) }}
                  </div>


                </td>
              </ng-container>

              <ng-container matColumnDef="appliesTo">
                <th mat-header-cell *matHeaderCellDef>Applies To</th>
                <td mat-cell *matCellDef="let template">{{ appliesToDescription(template.range) }}</td>
              </ng-container>

              <ng-container matColumnDef="age">
                <th mat-header-cell *matHeaderCellDef>Age</th>
                <td mat-cell *matCellDef="let template">{{ ageDescription(template.range) }}</td>
              </ng-container>

              <ng-container matColumnDef="classification">
                <th mat-header-cell *matHeaderCellDef>Match</th>
                <td mat-cell *matCellDef="let template">
                  <mat-chip-set>
                    <mat-chip class="classification-chip" [ngClass]="template.classification">
                      <mat-icon>{{ classificationIcon(template.classification) }}</mat-icon>
                      <span>{{ classificationLabel(template.classification) }}</span>
                    </mat-chip>
                  </mat-chip-set>
                </td>
              </ng-container>

              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let template"  class="apply-range-column">
                  <button mat-flat-button color="primary" (click)="applyRange(template)"
                  style="width: max-content"
                  class="apply-range-button"
                    [disabled]="!canApplyRanges() || isTemplateApplying(template)"
                    [matTooltip]="canApplyRanges() ? 'Apply this range to the observation' : 'Save the observation first'">
                    @if(isTemplateApplying(template)) {
                      <mat-icon class="spin">hourglass_top</mat-icon>
                      <span>Applying…</span>
                    } @else {
                      <mat-icon class="me-4">playlist_add</mat-icon>
                      <span style="verical-align:middle;">Apply Range</span>
                    }
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
            </table>
          </div>
        }
      </div>
    </mat-tab>

    <mat-tab label="Manual Entry" [disabled]="(canManageManual$ | async) === false">
      <div class="tab-content" style="padding-top: 24px;" [formGroup]="manualForm">
        
        @if((canManageManual$ | async) === false) {
          <div class="empty-state">
            <p>Your organization has disabled manual reference range entries for your role.</p>
          </div>
        } @else {
          <div class="g-just-flex gap-16 flex-wrap">
            <mat-form-field appearance="outline" style="flex: 1 1 calc(50% - 8px)">
              <mat-label>Low Value</mat-label>
              <input matInput type="number" formControlName="lowValue">
              <span matTextSuffix *ngIf="observation?.valueQuantity?.unit">{{ observation?.valueQuantity?.unit }}</span>
            </mat-form-field>
            
            <mat-form-field appearance="outline" style="flex: 1 1 calc(50% - 8px)">
              <mat-label>High Value</mat-label>
              <input matInput type="number" formControlName="highValue">
              <span matTextSuffix *ngIf="observation?.valueQuantity?.unit">{{ observation?.valueQuantity?.unit }}</span>
            </mat-form-field>

            <mat-form-field appearance="outline" style="flex: 1 1 100%">
              <mat-label>Meaning / Type</mat-label>
              <mat-select formControlName="meaning">
                <mat-option *ngFor="let opt of rangeTypeOptions" [value]="opt.code">
                  {{ opt.display }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" style="flex: 1 1 100%">
              <mat-label>Interpretation Flag</mat-label>
              <mat-select formControlName="interpretation">
                <mat-option *ngFor="let opt of interpretationOptions" [value]="opt.code">
                  {{ opt.display }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div class="actions g-just-flex flex-end mt-16" style="margin-top: 16px; justify-content: flex-end;">
            <button mat-flat-button color="primary" (click)="saveManualRange()" [disabled]="manualSaving">
              @if(manualSaving) {
                <mat-icon class="spin">hourglass_top</mat-icon>
                <span>Saving...</span>
              } @else {
                <mat-icon>save</mat-icon>
                <span>Apply Manual Range</span>
              }
            </button>
          </div>
        }
      </div>
    </mat-tab>
  </mat-tab-group>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="close()">Close</button>
  <button mat-stroked-button color="primary" (click)="manageTemplates()">
    Manage Templates
  </button>
</mat-dialog-actions>
